import { useMemo } from 'react';
import { SectionHeader } from '@/components/ui';
import { useNow } from '@/hooks/useNow';
import { useSaleEventsList } from '@/hooks/useSaleEvent';
import { useHasSaleEvents } from '@/hooks/useSaleCountries';
import { DEFAULT_LOCALE, type Locale } from '@/seo/locales';
import {
  mapBackendSaleEventRows,
  nextSaleEvents,
  saleCalendarPath,
  saleEventPhase,
  type SaleEvent,
} from '@/seo/saleEvents';
import { SaleEventCard } from './SaleEventCard';

/** How many of the next events the home page shows. */
export const HOME_SALE_EVENTS = 3;

function Block({ events, now, locale, className }: { events: SaleEvent[]; now: Date; locale: Locale; className?: string }) {
  return (
    <section aria-label="Sale calendar" className={className}>
      <SectionHeader title="Sale calendar" to={saleCalendarPath(locale)} seeAllLabel="All sale dates" />
      {events.length > 0 && (
        <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((event) => (
            <li key={event.slug}>
              <SaleEventCard event={event} now={now} locale={locale} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Pakistan's block: the static config, no request (unchanged by MW8). */
function StaticEventsBlock({ locale, className }: { locale: Locale; className?: string }) {
  // A label that flips from "Upcoming" to "Live now" only needs minutes.
  const now = useNow(60_000);
  const events = nextSaleEvents(now, locale).slice(0, HOME_SALE_EVENTS);
  return <Block events={events} now={now} locale={locale} className={className} />;
}

/** Another country's block: its own published events from the backend (the static config is Pakistan's). */
function DataEventsBlock({ locale, className }: { locale: Locale; className?: string }) {
  const now = useNow(60_000);
  const { events: rows } = useSaleEventsList(locale);
  const events = useMemo(
    () =>
      mapBackendSaleEventRows(rows ?? [], now)
        .filter((event) => saleEventPhase(event, now, locale) !== 'ended')
        .slice(0, HOME_SALE_EVENTS),
    [rows, now, locale],
  );
  return <Block events={events} now={now} locale={locale} className={className} />;
}

/**
 * Home page block: a link to the sale calendar and the next few published
 * events (live or upcoming), each a plain link to its hub (plan W0.15:
 * "linked from home"). Pakistan reads its config (no request); another
 * country lists its own events from the backend. Only where the country has
 * published events (`locales { hasSaleEvents }`, MW8); the calendar link
 * stays even when no event is left in the season.
 */
export function SaleCalendarSection({
  locale = DEFAULT_LOCALE,
  className,
}: {
  locale?: Locale;
  className?: string;
}) {
  // A country without sale events has no calendar (its own events, once it publishes some).
  const enabled = useHasSaleEvents(locale);
  if (!enabled) return null;
  return locale.cc === DEFAULT_LOCALE.cc ? (
    <StaticEventsBlock locale={locale} className={className} />
  ) : (
    <DataEventsBlock locale={locale} className={className} />
  );
}

export default SaleCalendarSection;
